// Right-hand tool sidebar for the Designer, extracted verbatim from Designer.tsx.
// Purely presentational: it renders the Garment / Text / Design panels for the
// active tool and forwards every interaction back to the page via props. No
// behaviour change — the JSX is identical to the inline <aside> it replaced.
import type { Ref } from 'react'
import type { CatProduct } from '../../lib/catalog-types'
import type { Tool, GarmentTotals } from './helpers'
import InlineUploadLibrary, { type InlineUploadLibraryHandle, type UploadAsset } from '../../components/InlineUploadLibrary'
import type { SavedUpload } from '../../components/LibraryDrawer'

export interface DesignerSidebarProps {
  tool: Tool
  garments: CatProduct[]
  productSlug: string
  setProductSlug: (slug: string) => void
  product: CatProduct
  colourId: string | null
  setColourId: (id: string) => void
  needsSizes: boolean
  setNeedsSizes: (v: boolean) => void
  qty: Record<string, number>
  setQty: (q: Record<string, number>) => void
  totals: GarmentTotals
  sizesRef: Ref<HTMLDivElement>
  addText: () => void
  textDragMode: boolean
  setTextDragMode: (v: boolean) => void
  onPickFile: (e: React.ChangeEvent<HTMLInputElement>) => void
  hasUser: boolean
  libraryRef: Ref<InlineUploadLibraryHandle>
  onUseUpload: (u: SavedUpload) => void
  setLibraryOpen: (v: boolean) => void
  onContinue: () => void
}

export default function DesignerSidebar({
  tool, garments, productSlug, setProductSlug, product, colourId, setColourId,
  needsSizes, setNeedsSizes, qty, setQty, totals, sizesRef, addText,
  textDragMode, setTextDragMode, onPickFile, hasUser, libraryRef, onUseUpload, setLibraryOpen,
  onContinue,
}: DesignerSidebarProps) {
  return (
    <aside className="w-[340px] flex-shrink-0 border-l border-border bg-white overflow-y-auto">
      {tool === 'product' && (
        <section className="p-5 space-y-6">
          <div>
            <div className="eyebrow">Garment</div>
            <div className="space-y-1.5">
              {garments.map((g) => (
                <button key={g.id} onClick={() => setProductSlug(g.slug)}
                  className={`w-full text-left px-3 py-2.5 rounded border transition-colors ${productSlug === g.slug ? 'border-gold bg-gold/5' : 'border-border hover:border-ink-3'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-ink text-sm">{g.name}</span>
                    <span className="text-xs text-ink-3">from ${Math.min(...g.variants.map((v) => v.price)).toFixed(0)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="eyebrow">Colour</div>
            <div className="flex flex-wrap gap-2">
              {product.colours.map((c) => (
                <button key={c.id} title={c.name} onClick={() => setColourId(c.id)}
                  className={`w-7 h-7 rounded-full ring-2 ring-offset-2 ring-offset-white transition-all ${colourId === c.id ? 'ring-ink' : 'ring-transparent'}`}
                  style={{ background: c.hex, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)' }} />
              ))}
            </div>
          </div>
          <div ref={sizesRef}>
            <div className="eyebrow">Sizes &amp; Quantity</div>
            {needsSizes && (
              <div className="rounded border border-amber-400 bg-amber-50 text-amber-900 text-xs px-3 py-2 mb-2 flex items-start gap-2 animate-pulse">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span><strong className="font-semibold">Pick at least one size + quantity</strong> below before you can submit.</span>
              </div>
            )}
            <div className="space-y-2">
              {product.variants.filter((v) => v.size).map((v) => (
                <div key={v.id} className={'flex items-center gap-2 text-sm rounded px-1.5 -mx-1.5 transition-colors ' + (needsSizes ? 'bg-amber-50/60' : '')}>
                  <span className="w-10 text-xs font-semibold text-ink uppercase">{v.size}</span>
                  <input type="number" min={0} value={qty[v.size!] ?? 0}
                    onChange={(e) => {
                      const n = Math.max(0, parseInt(e.target.value) || 0)
                      setQty({ ...qty, [v.size!]: n })
                      if (n > 0) setNeedsSizes(false)
                    }}
                    className={'w-14 px-2 py-1 bg-white border rounded text-ink text-sm focus:outline-none focus:border-gold ' + (needsSizes ? 'border-amber-500 ring-1 ring-amber-300' : 'border-border')} />
                  <span className="text-xs text-ink-3 ml-auto">${v.price.toFixed(2)} ea</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-border pt-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-ink-3">Total qty</span><span className="text-ink font-semibold">{totals.totalQty}</span></div>
            <div className="flex justify-between text-base"><span>Total</span><span className="text-ink font-bold">${totals.total.toFixed(2)}</span></div>
          </div>
          <button
            onClick={onContinue}
            className="w-full py-3.5 rounded-lg bg-brick hover:bg-brick-dark text-white font-bold text-sm uppercase tracking-[0.12em] flex items-center justify-center gap-2 transition-colors shadow-md">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Continue to Order
          </button>
        </section>
      )}

      {tool === 'text' && (
        <section className="p-5 space-y-4">
          <div className="eyebrow">Add text</div>
          <p className="text-ink-2 text-sm">Either drop a text layer at the print area, or drag out a text area for sized typing. The floating toolbar handles font, size, weight, alignment, and colour.</p>
          <button onClick={addText} className="btn-gold w-full justify-center">Add text layer</button>
          <button onClick={() => setTextDragMode(true)} disabled={textDragMode}
            className={'w-full justify-center text-sm py-2.5 rounded uppercase tracking-wide font-semibold border transition-colors ' +
              (textDragMode ? 'border-gold text-gold-dark bg-gold/10' : 'border-ink text-ink hover:bg-ink hover:text-white')}>
            {textDragMode ? 'Drag on the canvas…' : 'Drag to create text area'}
          </button>
          <p className="text-[11px] text-ink-3 leading-relaxed">Drag-to-create produces a fixed-width text box — perfect for paragraph copy where the alignment buttons matter.</p>
        </section>
      )}

      {tool === 'design' && (
        <section className="p-5 space-y-5">
          <div>
            <div className="eyebrow">Upload your design</div>
            <label className="block">
              <input type="file" accept="image/*" className="hidden" onChange={onPickFile} />
              <span className="block px-4 py-8 border-2 border-dashed border-border-2 rounded text-center text-ink-3 text-sm cursor-pointer hover:border-gold hover:text-ink transition-colors">
                <span className="block text-2xl mb-1">⬆</span>
                PNG, JPG, or SVG up to 10&nbsp;MB
              </span>
            </label>
            {/* Minimum art requirements + ownership disclaimer. */}
            <ul className="text-[11px] text-ink-3 mt-3 space-y-0.5 leading-snug">
              <li>· Minimum <strong className="text-ink-2">300 DPI</strong> for print quality</li>
              <li>· Recommended <strong className="text-ink-2">1000 × 1000 px</strong> or larger</li>
              <li>· Transparent PNG for best results on dark garments</li>
              <li>· You must own or have rights to the artwork</li>
            </ul>
            {hasUser && <p className="text-[11px] text-ink-3 mt-2">Uploads stay in your library. Press <strong className="text-ink-2">Save</strong> at the top to save your design.</p>}
          </div>

          <InlineUploadLibrary
            ref={libraryRef}
            onUse={(a: UploadAsset) => onUseUpload({ id: a.id, url: a.url, originalName: a.originalName, mimeType: 'image/*', size: 0, widthPx: a.widthPx, heightPx: a.heightPx, createdAt: a.createdAt })}
            onOpenFullLibrary={() => setLibraryOpen(true)}
          />
        </section>
      )}
    </aside>
  )
}
